/**
 * Socket.IO Client Singleton
 *
 * Manages the Socket.IO connection to the /conversation namespace.
 * Handles authentication via JWT and auto-reconnection.
 *
 * Reconnection policy is centralized in `ensureConnected()` — the single
 * entry point for "make sure the socket is alive". All triggers (app
 * foreground, screen focus, manual tap) funnel through it.
 *
 * Auth logic (token refresh, rejection tracking) is delegated to
 * SocketAuthManager for testability.
 */

import { io, Socket } from 'socket.io-client';
import { AppState, AppStateStatus, Platform } from 'react-native';
import { APP_VERSION } from '../config/constants';
import { create } from 'zustand';
import { getApiUrl } from '../config/constants';
import { getAuthProvider } from '../auth';
import { createLogger } from '../utils/logger';
import { SocketAuthManager } from './socketAuth';
import { networkMonitor } from './networkMonitor';

// ── Connection Status Store ─────────────────────────────────────────

export type ConnectionStatus = 'connected' | 'connecting' | 'disconnected' | 'failed' | 'session_expired';

interface SocketStatusState {
  status: ConnectionStatus;
  /** Timestamp when socket first disconnected (for time-based UX hints) */
  disconnectedAt: number | null;
}

/**
 * Zustand store exposing socket connection status for UI.
 * Used by ConnectionStatusBanner to show reconnecting state.
 */
export const useSocketStatus = create<SocketStatusState>()(() => ({
  status: 'disconnected',
  disconnectedAt: null,
}));

const log = createLogger('SocketIO');

let socket: Socket | null = null;
let appStateSubscription: ReturnType<typeof AppState.addEventListener> | null = null;
/** Unsubscribe for the NetInfo network-regain listener (see forceReconnect). */
let networkRegainUnsub: (() => void) | null = null;

/** Auth manager — adapts the app's AuthProvider to SocketAuthManager's interface */
const authManager = new SocketAuthManager(() => {
  const provider = getAuthProvider();
  return {
    async getSession() {
      const token = await provider.getAccessToken();
      return { data: { session: token ? { access_token: token } : null } };
    },
    async refreshSession() {
      const session = await provider.refreshSession();
      return {
        data: { session: session ? { access_token: session.accessToken } : null },
        error: session ? null : { message: 'Refresh failed' },
      };
    },
  };
});

/**
 * Derive the Socket.IO server URL from the API URL.
 * API URL is like "http://localhost:3000/api" — we need "http://localhost:3000".
 */
function getSocketUrl(): string {
  const apiUrl = getApiUrl();
  // Strip the /api path suffix to get the server origin
  return apiUrl.replace(/\/api\/?$/, '');
}

/**
 * Connect the Socket.IO client to the /conversation namespace.
 * If already connected, this is a no-op.
 */
export async function connectSocket(): Promise<void> {
  if (socket?.connected) {
    log.debug('Socket already connected');
    return;
  }

  const token = await authManager.getFreshToken();
  if (!token) {
    log.warn('Cannot connect socket — no auth token');
    return;
  }

  const serverUrl = getSocketUrl();

  // Disconnect existing socket before creating a new one
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
  }

  socket = io(`${serverUrl}/conversation`, {
    auth: {
      token,
      appVersion: APP_VERSION,
      platform: Platform.OS,
    },
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10000,
    reconnectionAttempts: 10,
    randomizationFactor: 0.3,
    timeout: 10000,
    autoConnect: true,
  });

  socket.on('connect', () => {
    log.info('Socket connected', { id: socket?.id });
    useSocketStatus.setState({ status: 'connected', disconnectedAt: null });
    authManager.resetRejections();
  });

  socket.on('disconnect', (reason) => {
    log.info('Socket disconnected', { reason });
    useSocketStatus.setState({ status: 'disconnected', disconnectedAt: Date.now() });
  });

  socket.on('connect_error', async (err) => {
    const errorCode = (err as Error & { data?: { code?: string } }).data?.code;
    log.warn('Socket connect error', { message: err.message, code: errorCode });
    useSocketStatus.setState({ status: 'connecting' });

    const result = await authManager.handleConnectError(errorCode);

    switch (result.action) {
      case 'refresh':
        if (socket) socket.auth = { token: result.token };
        break;
      case 'stop':
        log.warn('Max auth rejections reached, stopping reconnection', {
          rejections: authManager.rejectionCount,
        });
        socket?.disconnect();
        useSocketStatus.setState({ status: 'failed' });
        break;
      case 'none':
        // Auth error but can't get a fresh token — session is gone
        log.warn('No auth token available, session expired');
        socket?.disconnect();
        useSocketStatus.setState({ status: 'session_expired' });
        break;
      // 'retry' — transport error, let Socket.IO retry with the current token
    }
  });

  socket.io.on('reconnect_attempt', () => {
    useSocketStatus.setState({ status: 'connecting' });
    // Note: token refresh is handled by connect_error, not here.
    // This handler is sync — Socket.IO doesn't await async callbacks,
    // so an async token refresh here would race with the connection attempt.
  });

  socket.io.on('reconnect', (attempt: number) => {
    log.info('Socket reconnected after retry', { attempt });
  });

  socket.io.on('reconnect_error', (err: Error) => {
    log.debug('Socket reconnect attempt failed', { message: err.message });
  });

  socket.io.on('reconnect_failed', () => {
    log.warn('Socket reconnection failed after max attempts');
    useSocketStatus.setState({ status: 'failed' });
  });

  // Listen for app state changes to reconnect when foregrounded
  if (!appStateSubscription) {
    appStateSubscription = AppState.addEventListener('change', handleAppStateChange);
  }

  // Listen for network regain (Wi-Fi restore, interface handoff) to force a
  // fresh transport. Distinct from the foreground trigger above: after a blip
  // `socket.connected` can still read `true` on a dead transport, so we bypass
  // the `ensureConnected` fast-path and reset unconditionally.
  if (!networkRegainUnsub) {
    networkRegainUnsub = networkMonitor.onNetworkRegained(() => {
      log.info('Network regained, forcing socket reconnect');
      forceReconnect().catch((err) => log.warn('forceReconnect failed', err));
    });
  }

  // Peer listeners are registered lazily by the conversation store
  // to avoid circular imports (store imports socket, socket can't import store).
  // The store calls registerPeerListeners() on socket connect via onSocketReady.
  if (onSocketReadyCallback) {
    onSocketReadyCallback(socket);
  }

  log.info('Socket.IO client initialized', { serverUrl });
}

/**
 * Register a callback invoked when a new socket connection is established.
 * Used by the conversation store to set up peer listeners.
 */
let onSocketReadyCallback: ((socket: Socket) => void) | null = null;

export function onSocketReady(callback: (socket: Socket) => void): void {
  onSocketReadyCallback = callback;
  // If socket is already connected, invoke immediately
  if (socket?.connected) {
    callback(socket);
  }
}

/**
 * Disconnect the Socket.IO client. Called on logout.
 */
export function disconnectSocket(): void {
  if (appStateSubscription) {
    appStateSubscription.remove();
    appStateSubscription = null;
  }

  if (networkRegainUnsub) {
    networkRegainUnsub();
    networkRegainUnsub = null;
  }

  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
    useSocketStatus.setState({ status: 'disconnected' });
    log.info('Socket disconnected and cleaned up');
  }
}

/**
 * Get the current socket instance (may be null if not connected).
 */
export function getSocket(): Socket | null {
  return socket;
}

/**
 * Check if the socket is currently connected.
 */
export function isSocketConnected(): boolean {
  return socket?.connected ?? false;
}

/**
 * Ensure the socket is connected. Single entry point for all reconnection
 * triggers (app foreground, screen focus, manual "Tap to reconnect").
 *
 * - No-op if already connected
 * - Refreshes the auth token before reconnecting
 * - Interrupts any in-progress Socket.IO reconnection cycle so the
 *   fresh token takes effect immediately
 */
export async function ensureConnected(): Promise<void> {
  if (!socket) return;
  if (socket.connected) return;

  authManager.resetRejections();
  const freshToken = await authManager.getFreshToken();
  if (freshToken) {
    // Disconnect first to interrupt any in-progress reconnection cycle,
    // then reconnect with the fresh token and a reset attempt counter.
    socket.disconnect();
    socket.auth = { token: freshToken };
    socket.connect();
    useSocketStatus.setState({ status: 'connecting', disconnectedAt: Date.now() });
  } else {
    useSocketStatus.setState({ status: 'session_expired' });
  }
}

/**
 * Force a fresh transport, regardless of the (possibly stale) `connected` flag.
 *
 * Triggered by a NetInfo network-regain edge (Wi-Fi restore, Wi-Fi↔cellular
 * handoff). After such a transition Socket.IO may still report `connected`
 * until its ~20s ping timeout elapses, even though the underlying TCP
 * connection is dead — so unlike `ensureConnected()`, this deliberately skips
 * the `if (socket.connected) return` fast-path and tears the engine down and
 * rebuilds it with a fresh token.
 */
export async function forceReconnect(): Promise<void> {
  if (!socket) return;

  authManager.resetRejections();
  const freshToken = await authManager.getFreshToken();
  if (!freshToken) {
    log.warn('Cannot force reconnect — no auth token, session expired');
    useSocketStatus.setState({ status: 'session_expired' });
    return;
  }

  socket.auth = { token: freshToken };
  socket.disconnect(); // drops the underlying (possibly dead) engine transport
  socket.connect(); // rebuilds from scratch
  useSocketStatus.setState({ status: 'connecting', disconnectedAt: Date.now() });
}

/**
 * Handle app state changes — reconnect when coming to foreground.
 */
async function handleAppStateChange(nextAppState: AppStateStatus): Promise<void> {
  if (nextAppState === 'active' && socket && !socket.connected) {
    log.info('App foregrounded, reconnecting socket');
    await ensureConnected();
  }
}
